/**
 * The three web need bands, as filters.
 *
 * The same three numbers used to sit in a status bar above the list where they
 * could only be read. That was the wrong shape for them: "22 357 companies badly
 * need a website" is not a fact you note and move on from, it is the list you
 * came to work, and getting to it meant opening the advanced panel and typing 70
 * into a box whose threshold nobody remembers.
 *
 * So the counts became buttons. Pressing one sets exactly the bounds
 * `webNeedBand` colours the table by, which is why the thresholds are imported
 * rather than written here - a band that filtered on 65 while the pill beside it
 * turned amber at 70 would be a table disagreeing with its own filter.
 *
 * The bounds deliberately exclude "not computed yet": every band is a claim
 * about a company somebody has actually looked at.
 */
import { useT } from "../../../../i18n/I18nProvider";
import { WEB_NEED_HIGH, WEB_NEED_MID, type WebNeedBand } from "../../../../lib/webNeed";
import { fmtNum } from "../../../ui";

/** The scored bands, widest score first. `none` is not one of them - it is the
 *  absence of a score, and there is nothing to call in it. */
type Band = Exclude<WebNeedBand, "none">;

/** Band to the `webNeedMin`/`webNeedMax` pair that selects it. Strings, because
 *  that is what the filter carries all the way to the query string. */
export const BAND_BOUNDS: Record<Band, { min: string; max: string }> = {
  high: { min: String(WEB_NEED_HIGH), max: "" },
  mid: { min: String(WEB_NEED_MID), max: String(WEB_NEED_HIGH - 1) },
  low: { min: "1", max: String(WEB_NEED_MID - 1) },
};

const BANDS: Band[] = ["high", "mid", "low"];

/** Which band the current bounds are, or null for "no band / something typed by
 *  hand". Compared on both ends, so a hand-typed 70-90 is correctly NOT shown as
 *  the high band with its own upper limit thrown away. */
export function activeBand(min: string, max: string): Band | null {
  return BANDS.find((b) => BAND_BOUNDS[b].min === min && BAND_BOUNDS[b].max === max) ?? null;
}

export function WebNeedBands({
  counts,
  min,
  max,
  onPick,
}: {
  /** Live counts from the ranking pass, or null while it is being fetched. */
  counts: { high: number; mid: number; low: number } | null;
  min: string;
  max: string;
  /** Null clears the band. */
  onPick: (bounds: { min: string; max: string } | null) => void;
}) {
  const t = useT();
  const active = activeBand(min, max);

  return (
    <div className="crm-bands">
      <span className="crm-bands-l">{t("ariregister.webNeed.title")}</span>
      {BANDS.map((band) => {
        const on = active === band;
        return (
          <button
            key={band}
            type="button"
            aria-pressed={on}
            // Pressing the band that is already on clears it, the way every
            // other toggle filter on this page does.
            onClick={() => onPick(on ? null : BAND_BOUNDS[band])}
            className={`crm-band is-${band}${on ? " is-on" : ""}`}
          >
            <i />
            {t(`ariregister.webNeed.band.${band}`)}
            {counts ? <span className="crm-tnum crm-band-n">{fmtNum(counts[band])}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
